import { useState, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";

export type CollapsibleToolGroupProps = {
    title: string;
    icon: LucideIcon;
    defaultOpen?: boolean;
    /**
     * Controlled-open override. When set, the group is open iff this is true and
     * `onOpenChange` is notified of toggle requests (the internal state is
     * ignored). Leave undefined for the default uncontrolled behavior.
     */
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    children: ReactNode;
};

export function CollapsibleToolGroup({
    title,
    icon: Icon,
    defaultOpen = true,
    open: controlledOpen,
    onOpenChange,
    children,
}: CollapsibleToolGroupProps) {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
    const open = controlledOpen ?? uncontrolledOpen;
    const setOpen = (next: boolean) => {
        onOpenChange?.(next);
        if (controlledOpen === undefined) setUncontrolledOpen(next);
    };
    return (
        <div
            className={[
                "control-collapsible",
                !open ? "control-collapsible--collapsed" : "",
            ]
                .filter(Boolean)
                .join(" ")}
        >
            <button
                type="button"
                className="control-collapsible__toggle"
                onClick={() => setOpen(!open)}
                aria-expanded={open}
            >
                <span className="control-collapsible__toggle-start">
                    <Icon
                        className="control-collapsible__title-icon"
                        size={18}
                        strokeWidth={2}
                        aria-hidden
                    />
                    <span>{title}</span>
                </span>
                <svg
                    className="control-collapsible__chevron"
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    aria-hidden
                >
                    <path
                        d="M6 9l6 6 6-6"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </button>
            {open && (
                <div className="control-collapsible__body">
                    <div className="tool-input-group">{children}</div>
                </div>
            )}
        </div>
    );
}
